import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import styles from './Bonuses.module.scss';
import { useDispatch } from 'react-redux';
import { setUserData } from '@/store';
import withAuth from '@/providers/WithAuth';
import { Layout } from '@/layout';
import { Palettes, SettingsType } from '@/types';
import { fetchData } from '@/services/fetchData';
import { PageTitle } from '@/components/PageTitle';
import { PaginationRoutes } from '@/components/PaginationRoutes';
import { PaginationTabs } from '@/components/PaginationTabs';
import { useEffect, useState } from 'react';
import { axiosPersonal } from '@/services/axiosPersonal';
import { Container } from '@/components/Container';
import { BonusesCard } from '@/components/BonusesCard';
import { ACCESS_TOKEN_KEY, EMPTY_META, REFRESH_TOKEN_KEY } from '@/constants';
import cn from 'classnames';
import { Loader } from '@/components/Loader/Loader';
import { deleteToken } from '@/services/deleteToken';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';

const PageArrow = ({
  disabled,
  onClick,
  left,
}: {
  disabled: boolean;
  onClick: () => void;
  left?: boolean;
}) => {
  const arrowClassName = cn(styles.buttonContainer, {
    [styles.reverse]: left,
    [styles.disabled]: disabled,
  });

  const handleClick = () => {
    if (disabled) return;
    onClick();
  };

  return (
    <div onClick={handleClick} className={arrowClassName}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.25227 11.1747C5.15156 11.1713 5.0526 11.1474 4.96147 11.1044C4.87033 11.0614 4.78893 11.0003 4.72227 10.9247C4.58182 10.7841 4.50293 10.5935 4.50293 10.3947C4.50293 10.196 4.58182 10.0054 4.72227 9.86477L7.72227 6.86477L4.72227 3.86477C4.66294 3.72076 4.64992 3.56185 4.68502 3.4101C4.72012 3.25836 4.80159 3.1213 4.91812 3.01796C5.03465 2.91462 5.18046 2.85013 5.33532 2.83343C5.49017 2.81673 5.64638 2.84865 5.78227 2.92477L9.28227 6.42477C9.42272 6.5654 9.50161 6.75602 9.50161 6.95477C9.50161 7.15352 9.42272 7.34415 9.28227 7.48477L5.78227 10.9247C5.7156 11.0003 5.6342 11.0614 5.54307 11.1044C5.45193 11.1474 5.35298 11.1713 5.25227 11.1747Z"
          fill={disabled ? '#a5a9ad' : '#18232E'}
        />
      </svg>
    </div>
  );
};

const PageArrowForced = ({
  disabled,
  onClick,
  left,
}: {
  disabled: boolean;
  onClick: () => void;
  left?: boolean;
}) => {
  const arrowClassName = cn(styles.buttonContainer, {
    [styles.reverse]: left,
    [styles.disabled]: disabled,
  });

  const handleClick = () => {
    if (disabled) return;
    onClick();
  };

  return (
    <div onClick={handleClick} className={arrowClassName}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.69343 11.1469C7.78496 11.1849 7.88316 11.2042 7.98226 11.2037C8.08355 11.2003 8.18307 11.1763 8.27472 11.133C8.36638 11.0898 8.44824 11.0283 8.51529 10.9523L12.0353 7.4926C12.1766 7.35117 12.2559 7.15946 12.2559 6.95957C12.2559 6.75969 12.1766 6.56797 12.0353 6.42654L8.51529 2.90654C8.376 2.82334 8.2136 2.78726 8.05219 2.80367C7.89077 2.82008 7.73895 2.8881 7.61926 2.99763C7.49956 3.10716 7.41838 3.25236 7.38775 3.41169C7.35712 3.57102 7.37868 3.73598 7.44923 3.88208L10.4664 6.89923L7.44923 9.91638C7.30798 10.0579 7.22864 10.2496 7.22864 10.4495C7.22864 10.6493 7.30798 10.8411 7.44923 10.9825C7.51887 11.053 7.60189 11.1089 7.69343 11.1469ZM1.94793 10.9523C2.07623 11.0986 2.25675 11.1889 2.45079 11.2037C2.64484 11.1889 2.82535 11.0986 2.95365 10.9523L6.47365 7.4926C6.61491 7.35117 6.69425 7.15946 6.69425 6.95957C6.69425 6.75968 6.61491 6.56797 6.47365 6.42654L2.95365 2.90654C2.8129 2.83932 2.65476 2.81738 2.50103 2.84376C2.34729 2.87014 2.20551 2.94353 2.09522 3.05382C1.98492 3.16412 1.91153 3.3059 1.88516 3.45963C1.85878 3.61337 1.88071 3.7715 1.94793 3.91225L4.96508 6.9294L1.94793 9.94655C1.82424 10.0849 1.75586 10.2639 1.75586 10.4495C1.75586 10.635 1.82424 10.814 1.94793 10.9523Z"
          fill={disabled ? '#a5a9ad' : '#18232E'}
        />
      </svg>
    </div>
  );
};

type BonusesPageType = {
  balance: {
    available: number;
    spent: number;
    total: number;
  };
  list: {
    amount: string;
    availabilityDate: string;
    burnDate: string;
    comment: string;
    date: string;
    status: string;
  }[];
};

const Bonuses = ({
  menu,
  translations,
  common,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const dispatch = useDispatch();
  const [pageData, setPageData] = useState<BonusesPageType>({
    balance: {
      available: 0,
      spent: 0,
      total: 0,
    },
    list: [],
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 8;
  const { isMobile } = useAdaptiveWidth();

  useEffect(() => {
    const fetchPageData = async () => {
      const response: BonusesPageType = await axiosPersonal({
        url: '/point/list',
      });

      const data = {
        balance: response.balance,
        list: response.list,
      };

      setPageData(data);
      setLoading(false);
      setTotalPages(Math.ceil(response.list.length / itemsPerPage));
    };

    fetchPageData();
  }, []);

  const logOut = () => {
    deleteToken();
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    dispatch(setUserData(null));
  };

  const getCurrentPageItems = () => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, pageData.list.length);
    return pageData.list.slice(startIndex, endIndex);
  };

  const setFirstPage = () => {
    setCurrentPage(0);
  };

  const nextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  const prevPage = () => {
    if (currentPage === 0) return;
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const setLastPage = () => {
    setCurrentPage(totalPages - 1);
  };

  const renderPageNumbers = () =>
    Array.from({ length: totalPages }, (_, index) => (
      <div
        key={index + 1}
        onClick={() => {
          setCurrentPage(index);
        }}
        className={
          currentPage === index
            ? styles.paginationActive
            : styles.paginationPage
        }
      >
        {index + 1}
      </div>
    ));

  return (
    <Layout data={menu} meta={EMPTY_META}>
      <main className={styles.page}>
        <PaginationRoutes
          routes={[{ link: '/profile', text: common.profileLink }]}
        />
        <PageTitle text={common.profileTitle} />
        <PaginationTabs
          tabs={[
            {
              text: common.linkHome,
              link: '/profile',
            },
            {
              text: common.linkAboutMe,
              link: '/profile/about-me',
            },
            {
              text: common.linkContacts,
              link: '/profile/contacts',
            },
            {
              text: common.linkLogOut,
              link: '/auth/login',
              onClick: logOut,
              palette: Palettes.Primary,
            },
          ]}
        />
        {pageData.balance && (
          <Container title={translations.bonusesTitle}>
            <div className={styles.cards}>
              <BonusesCard
                title={translations.available}
                count={pageData.balance.available}
              />
              <BonusesCard
                title={translations.waiting}
                count={pageData.balance.spent}
                palette={Palettes.Secondary}
              />
              <BonusesCard
                title={translations.received}
                count={pageData.balance.total}
                palette={Palettes.Secondary}
              />
            </div>
          </Container>
        )}
        <Container title={translations.historyTitle}>
          <div className={cn(styles.table, { [styles.hidden]: loading })}>
            <div className={styles.tableTitle}>
              <p className={styles.tableTitleCol}>
                {isMobile ? translations.dateMob : translations.date}
              </p>
              <p className={styles.tableTitleCol}>{translations.desc}</p>
              <p className={styles.tableTitleCol}>
                {isMobile ? translations.countMob : translations.count}
              </p>
              <p className={styles.tableTitleCol}>{translations.deleteDate}</p>
              <p className={styles.tableTitleCol}>
                {translations.availableDate}
              </p>
              <p className={styles.tableTitleCol}>{translations.status}</p>
            </div>
            {loading && <Loader />}
            {getCurrentPageItems().map((item, index) => (
              <div
                className={styles.tableRow}
                key={item.availabilityDate + index}
              >
                <p className={styles.date}>
                  {new Date(item.date).toLocaleDateString('ru-RU')}
                </p>
                <p className={styles.comment}>
                  {item.comment || 'Оплата покупки билетов'}
                </p>
                <p
                  className={`${styles.amount} ${
                    item.amount.startsWith('-') ? '' : styles.highlight
                  }`}
                >
                  {item.amount}
                </p>
                <p className={styles.burnDate}>
                  {item.burnDate
                    ? new Date(item.burnDate).toLocaleDateString('ru-RU')
                    : '—'}
                </p>
                <p className={styles.availabilityDate}>
                  {new Date(item.availabilityDate).toLocaleDateString('ru-RU')}
                </p>
                <p
                  className={`${styles.status} ${
                    item.amount.startsWith('-') ? '' : styles.highlight
                  }`}
                >
                  {item.status}
                </p>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <PageArrowForced
                onClick={setFirstPage}
                disabled={currentPage === 0}
                left
              />
              <div className={styles.paginationNumbers}>
                {renderPageNumbers()}
              </div>
              <PageArrow onClick={prevPage} disabled={currentPage === 0} left />
              <PageArrow
                onClick={nextPage}
                disabled={currentPage === totalPages - 1}
              />
              <PageArrowForced
                onClick={setLastPage}
                disabled={currentPage === totalPages - 1}
              />
            </div>
          )}
        </Container>
      </main>
    </Layout>
  );
};

export async function getServerSideProps({
  locale,
}: GetServerSidePropsContext) {
  const menu: SettingsType = await fetchData({
    url: `/settings/view`,
    locale,
  });

  return {
    props: {
      menu,
      translations: menu.translations['/history/bonuses'],
      common: menu.translations.common,
    },
  };
}

export default withAuth(Bonuses);
